import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import AppButton from "@/features/line-sheet-sets/app-button";
import PathPickFilterToggleButton from "@/features/pathpick/components/ui/PathPickFilterToggleButton";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // 필터가 하나라도 걸려 있는지 — 초기화 액션을 그때만 노출한다.
  isFilterActive: boolean;
  onReset: () => void;
  // 두 모달이 서로 다른 초기화 문구 키(product_requests_modal / synced_products_modal)를 쓴다.
  resetLabel: string;
  children: React.ReactNode;
}

interface GroupProps {
  label: string;
  children: React.ReactNode;
}

interface TriggerButtonProps {
  // 전체가 아닌 필터 축의 수. 0이면 배지를 띄우지 않는다.
  appliedCount: number;
  onClick: () => void;
  // 옆 검색창과 높이를 맞출 때 "auto"를 넘긴다 (PathPickFilterToggleButton의 height와 같다).
  height?: string;
}

// 필터 모달을 여는 버튼 — 모양과 모바일 분기는 공용 버튼이 갖고 있다.
// 카드 안에서 필터를 펼치는 화면(주문 내역)도 같은 버튼을 쓴다.
const TriggerButton = ({
  appliedCount,
  onClick,
  height,
}: TriggerButtonProps) => {
  return (
    <PathPickFilterToggleButton
      appliedCount={appliedCount}
      onClick={onClick}
      height={height}
    />
  );
};

// 필터 축 하나 — 라벨 위, 탭 아래로 세로 배치한다. 좁은 폭에서 세로 공간을 아끼려고
// 모달로 옮긴 것이므로 인라인 줄과 달리 라벨을 옆에 두지 않는다.
const Group = ({ label, children }: GroupProps) => {
  return (
    <Flex flexDirection={"column"} gap={"8px"}>
      <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
        {label}
      </Text>
      {children}
    </Flex>
  );
};

// 등록 신청 내역 두 모달(신청 상품 내역 / 상품별 처리 내역)이 공유하는 필터 모달.
// 좁은 폭에서 인라인 필터 줄이 세로를 5~6줄씩 잡아먹어 목록이 밀리는 것을 막으려고,
// 같은 필터를 버튼 뒤로 옮겨 담는다.
//
// 필터 상태는 부모 모달이 그대로 소유한다 — 여기서 고른 값은 인라인 줄과 같은 상태를
// 즉시 바꾸므로(서버 조회 파라미터라 원래도 즉시 재조회다) 하단 확인은 닫기 역할만 한다.
function PathPickRequestFilterModal({
  isOpen,
  onClose,
  isFilterActive,
  onReset,
  resetLabel,
  children,
}: Props) {
  const { t } = useI18nHelper();

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      // minWidth가 maxWidth를 이기고, 가운데 정렬된 모달은 왼쪽으로 넘치면 스크롤로도
      // 닿을 수 없어 화면 폭으로 클램프한다.
      minWidth={"min(420px, 90dvw)"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t("path_pick:filter.title")}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"16px"}>
          {children}

          {/* 초기화는 좌측 보조 액션이라 공용 Footer 대신 자체 액션 줄로 처리한다.
              초기화가 없을 때도 확인은 오른쪽에 붙어 있어야 해서 정렬을 유지한다. */}
          <Flex
            justifyContent={isFilterActive ? "space-between" : "flex-end"}
            alignItems={"center"}
            gap={"8px"}
          >
            {isFilterActive && (
              <Text
                as={"button"}
                type={"button"}
                textStyle={"body-sm-medium"}
                color={"#444440"}
                _hover={{ color: "#1272EF" }}
                onClick={onReset}
              >
                {resetLabel}
              </Text>
            )}
            <AppButton variant={"primary"} onClick={onClose}>
              <Text textStyle={"body-sm-bold"}>
                {t("path_pick:filter.confirm")}
              </Text>
            </AppButton>
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
    </CustomApprovalModal>
  );
}

PathPickRequestFilterModal.Group = Group;
PathPickRequestFilterModal.TriggerButton = TriggerButton;

export default PathPickRequestFilterModal;
